import React from 'react';
import { GenericCard } from '@ui-components/Card/GenericCard';
import type { ReportSummary } from '../hooks/useFortiFlexData';

interface SummaryCardsProps {
    summary: ReportSummary;
}

function fmt(n: number | null | undefined): string {
    if (n === null || n === undefined) return 'Pending';
    return n.toLocaleString('en-US', { maximumFractionDigits: 2 });
}

interface MetricCardProps {
    label: string;
    value: string;
    sub: string;
    highlight?: boolean;
}

const MetricCard: React.FC<MetricCardProps> = ({ label, value, sub, highlight }) => (
    <GenericCard
        isCollapsible={false}
        variant="primary"
        headerColor={highlight ? 'warning' : undefined}
        bodyContent={
            <div className="text-center">
                <span className="text-xs font-medium text-semantic-text-secondary uppercase tracking-wider">
                    {label}
                </span>
                <p className={`text-xl font-bold mt-1 ${highlight ? 'text-semantic-text-warning' : 'text-semantic-text-primary'}`}>
                    {value}
                </p>
                <span className="text-xs text-semantic-text-secondary">{sub}</span>
            </div>
        }
    />
);

const SummaryCards: React.FC<SummaryCardsProps> = ({ summary }) => {
    const isAlert = summary.alertLevel === 'WARNING' || summary.alertLevel === 'CRITICAL';

    return (
        <div className="space-y-4 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard
                    label="Total Points"
                    value={fmt(summary.pointsTotal)}
                    sub="points"
                />
                <MetricCard
                    label="Consumed"
                    value={fmt(summary.pointsUsed)}
                    sub="cumulative points"
                />
                <MetricCard
                    label="Available"
                    value={fmt(summary.pointsRemaining)}
                    sub="remaining points"
                />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <MetricCard
                    label="Daily Consumption"
                    value={fmt(summary.burnRateDaily)}
                    sub={
                        summary.dailyDataPending
                            ? 'not published by Fortinet yet'
                            : 'pts/day'
                    }
                />
                <MetricCard
                    label="Monthly Rate (proj.)"
                    value={fmt(summary.burnRate30d)}
                    sub="pts/month (daily x 30)"
                />
                <MetricCard
                    label="Estimated Exhaustion"
                    value={
                        summary.monthsRemaining >= 999
                            ? 'N/A'
                            : `~${summary.monthsRemaining} months`
                    }
                    sub={summary.projectedExhaustionDate || 'No exhaustion projected'}
                    highlight={isAlert}
                />
            </div>
        </div>
    );
};

export default SummaryCards;
